import React, { useRef, useEffect } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import FileAttachmentPreview, {
  FileAttachment,
} from "@/polymet/components/file-attachment-preview";

export interface Message {
  id: string;
  content: string;
  sender: {
    id: string;
    name: string;
    avatar?: string;
    role: "buyer" | "seller" | "system";
  };
  timestamp: string;
  attachments?: FileAttachment[];
  isRead?: boolean;
}

interface MessageThreadProps {
  messages: Message[];
  currentUserId: string;
  onMessageRead?: (messageId: string) => void;
}

export default function MessageThread({
  messages,
  currentUserId,
  onMessageRead,
}: MessageThreadProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Mark unread messages as read when they appear in the viewport
  useEffect(() => {
    const unreadMessages = messages.filter(
      (msg) => !msg.isRead && msg.sender.id !== currentUserId
    );

    if (unreadMessages.length > 0 && onMessageRead) {
      unreadMessages.forEach((msg) => {
        onMessageRead(msg.id);
      });
    }
  }, [messages, currentUserId, onMessageRead]);

  // Format timestamp
  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffDays = Math.floor(
      (now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24)
    );

    if (diffDays === 0) {
      return date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
    } else if (diffDays === 1) {
      return `Yesterday at ${date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
    } else if (diffDays < 7) {
      return `${date.toLocaleDateString([], { weekday: "short" })} at ${date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
    } else {
      return date.toLocaleDateString([], {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    }
  };

  // Group messages by date
  const groupMessagesByDate = () => {
    const groups: { date: string; messages: Message[] }[] = [];

    messages.forEach((message) => {
      const messageDate = new Date(message.timestamp).toLocaleDateString();
      const existingGroup = groups.find((group) => group.date === messageDate);

      if (existingGroup) {
        existingGroup.messages.push(message);
      } else {
        groups.push({
          date: messageDate,
          messages: [message],
        });
      }
    });

    return groups;
  };

  const messageGroups = groupMessagesByDate();

  // Format date for date separators
  const formatDateSeparator = (dateString: string) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return "Today";
    } else if (date.toDateString() === yesterday.toDateString()) {
      return "Yesterday";
    } else {
      return date.toLocaleDateString([], {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
      });
    }
  };

  return (
    <div className="flex flex-col space-y-4 p-4 overflow-y-auto max-h-[600px]">
      {messageGroups.map((group, groupIndex) => (
        <div key={group.date} className="space-y-4">
          <div className="flex justify-center">
            <div className="bg-muted px-3 py-1 rounded-full text-xs text-muted-foreground">
              {formatDateSeparator(group.date)}
            </div>
          </div>

          {group.messages.map((message, messageIndex) => {
            const isCurrentUser = message.sender.id === currentUserId;
            const isSystem = message.sender.role === "system";

            if (isSystem) {
              return (
                <div key={message.id} className="flex justify-center">
                  <div className="bg-muted px-4 py-2 rounded-full text-sm text-muted-foreground max-w-[80%]">
                    {message.content}
                  </div>
                </div>
              );
            }

            return (
              <div
                key={message.id}
                className={`flex ${isCurrentUser ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`flex gap-2 max-w-[80%] ${isCurrentUser ? "flex-row-reverse" : "flex-row"}`}
                >
                  <Avatar className="h-8 w-8">
                    {message.sender.avatar ? (
                      <AvatarImage
                        src={message.sender.avatar || ''}
                        alt={message.sender.name || 'User'}
                      />
                    ) : (
                      <AvatarFallback>
                        {(message.sender.name && typeof message.sender.name === 'string') 
                          ? message.sender.name.charAt(0).toUpperCase() 
                          : 'U'}
                      </AvatarFallback>
                    )}
                  </Avatar>

                  <div
                    className={`flex flex-col ${isCurrentUser ? "items-end" : "items-start"}`}
                  >
                    <div
                      className={`px-4 py-2 rounded-lg ${
                        isCurrentUser
                          ? "bg-primary text-primary-foreground"
                          : "bg-secondary text-secondary-foreground"
                      }`}
                    >
                      {message.content && (
                        <p className="text-sm">{message.content}</p>
                      )}

                      {message.attachments &&
                        message.attachments.length > 0 && (
                          <div
                            className={`mt-2 space-y-2 ${message.content ? "pt-2 border-t" : ""}`}
                          >
                            {message.attachments.map((file) => (
                              <FileAttachmentPreview
                                key={file.id}
                                file={file}
                                showRemoveButton={false}
                              />
                            ))}
                          </div>
                        )}
                    </div>

                    <span className="text-xs text-muted-foreground mt-1">
                      {formatTimestamp(message.timestamp)}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ))}

      {/* Empty div for scrolling to bottom */}
      <div ref={messagesEndRef} />
    </div>
  );
}
